/**
 * Which page this URL means, for this person.
 *
 * Pulled out of App.tsx's `Route()` because the answer now depends on
 * `isAdmin`, which arrives from /api/me after first paint — and this package
 * has no React rendering test setup (no jsdom, no testing-library; see
 * ProvisioningProgress's note). A pure function is both the testable shape and
 * the honest one: `null` means "not known yet", which is a third state, not a
 * falsy `false`.
 */
export type HubRoute =
  | { kind: "fleet" }
  | { kind: "instance-new" }
  | { kind: "instance-detail"; id: string }
  | { kind: "releases" }
  | { kind: "orgs" }
  | { kind: "org-new" }
  | { kind: "org-detail"; slug: string }
  | { kind: "channels" }
  | { kind: "loading" }
  | { kind: "redirect"; to: string };

export function routeFor(pathname: string, isAdmin: boolean | null): HubRoute {
  // Both audiences may read this one, so it needs no answer from /api/me.
  // Waiting would put a spinner in front of a decision that does not exist.
  // A trailing slash means the same page — without this, /releases/ falls
  // through to the operator-only fleet table below.
  if (pathname === "/releases" || pathname === "/releases/") return { kind: "releases" };

  if (isAdmin === null) return { kind: "loading" };
  if (!isAdmin) return { kind: "redirect", to: "/releases" };

  if (pathname === "/channels" || pathname === "/channels/") return { kind: "channels" };
  if (pathname === "/orgs" || pathname === "/orgs/") return { kind: "orgs" };
  if (pathname === "/orgs/new" || pathname === "/orgs/new/") return { kind: "org-new" };
  const org = pathname.match(/^\/orgs\/([^/]+)\/?$/);
  if (org?.[1]) return { kind: "org-detail", slug: org[1] };

  if (pathname === "/instances/new") return { kind: "instance-new" };

  const detail = pathname.match(/^\/instances\/([^/]+)\/?$/);
  if (detail?.[1]) return { kind: "instance-detail", id: detail[1] };

  return { kind: "fleet" };
}
